/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useEffect, useState } from 'react';
import {
  Button,
  Form,
  Input,
  Message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  TableColumnProps,
  Typography
} from '@arco-design/web-react';
import './index.scss';
import { get, post } from '../requestNew';
import { TopicItem, TopicAddItem } from './types';
import dayjs from 'dayjs';

const FormItem = Form.Item;
const Option = Select.Option;

const formItemLayout = {
  labelCol: {
    span: 6
  },
  wrapperCol: {
    span: 16
  }
};

interface TopicsProps {
  userInfo?: any;
}

function Topics({ userInfo = {} }: TopicsProps) {
  const [form] = Form.useForm<TopicAddItem>();
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [sessionList, setSessionList] = useState<{ label: string; value: string }[]>([]);
  const [gameSession, setGameSession] = useState('');
  const [topicNameFilter, setTopicNameFilter] = useState('');
  const [dataList, setDataList] = useState<TopicItem[]>([]);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);

  // 表格列配置
  const columns: TableColumnProps[] = [
    {
      title: '序号',
      width: 80,
      render: (_, record, index) => (current - 1) * pageSize + index + 1
    },
    {
      title: '是否可见',
      dataIndex: 'isShow',
      width: 100,
      render: (text, record) => {
        // 兼容后端可能返回数字或布尔值的情况
        const checked = typeof text === 'number' ? text === 1 : (text ?? false);
        return (
          <Switch
            checked={checked}
            onChange={val => handleIsShowChange(val, record)}
          />
        );
      }
    },
    {
      title: '话题名称',
      dataIndex: 'topicName',
      width: 200
    },
    {
      title: '赛季',
      dataIndex: 'gameSession',
      width: 120
    },
    {
      title: '讨论量',
      dataIndex: 'discussCount',
      width: 100,
      render: text => text || 0
    },
    {
      title: '浏览量',
      dataIndex: 'viewCount',
      width: 100,
      render: text => text || 0
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      width: 180,
      render: (text) => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '操作',
      dataIndex: 'action',
      width: 150,
      render: (_, record: TopicItem) => (
        <>
          <Button className="btn" type="text" onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title="提示"
            content="确认删除?"
            onOk={() => handleDelete(record.id!)}
          >
            <Button className="btn" type="text" status="danger">
              删除
            </Button>
          </Popconfirm>
        </>
      )
    }
  ];

  // 获取赛季列表
  const getSessionList = () => {
    get('/gameConfig/getUmsGameConfigList').then((res: any) => {
      if (res && res.data) {
        const list = res.data.map((item: any) => {
          const { gameSession } = item;
          return { label: gameSession, value: gameSession };
        });
        setSessionList(list);
      }
    });
  };

  // 获取数据列表
  const getList = (reset = false, page = current, size = pageSize) => {
    if (reset) {
      setCurrent(1);
    }
    setLoading(true);

    const params: any = {
      pageNum: reset ? 1 : page,
      pageSize: size
    };

    if (gameSession) {
      params.gameSession = gameSession;
    }
    if (topicNameFilter) {
      params.topicName = topicNameFilter;
    }

    get('/topic/list', params)
      .then((res: any) => {
        const { code, data, message } = res || {};
        if (code === 200) {
          if (data && typeof data === 'object' && 'list' in data) {
            // 将后端返回的数字转换为 boolean
            const list = (data.list || []).map((item: any) => ({
              ...item,
              isShow: item.isShow === 1 || item.isShow === true,
              isValid: item.isValid === 1 || item.isValid === true
            }));
            setDataList(list);
            setTotal(data.total || 0);
          } else {
            Message.error(message || '数据格式错误');
          }
        } else {
          Message.error(message || '获取数据失败');
        }
      })
      .catch(() => {
        Message.error('网络错误!');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // 切换是否可见状态
  const handleIsShowChange = (val: boolean, record: TopicItem) => {
    const swFlag = val ? 1 : 0;
    setLoading(true);

    post('/topic/update', { ...record, isShow: swFlag })
      .then((res: any) => {
        const { code, message } = res || {};
        if (code === 200) {
          Message.success('操作成功！');
          getList(false, current, pageSize);
        } else {
          Message.error(message || '操作失败');
        }
      })
      .catch(() => {
        Message.error('网络错误!');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // 新增
  const handleAdd = () => {
    setIsEdit(false);
    setEditId(null);
    form.resetFields();
    const defaultSession = gameSession || (sessionList.length > 0 ? sessionList[0].value : '');
    form.setFieldsValue({
      gameSession: defaultSession || undefined,
      isShow: true,
      isValid: true
    });
    setVisible(true);
  };

  // 编辑
  const handleEdit = (record: TopicItem) => {
    setIsEdit(true);
    setEditId(record.id || null);
    // 兼容后端可能返回数字或布尔值的情况
    const isShowValue = typeof record.isShow === 'number' ? record.isShow === 1 : (record.isShow ?? false);
    const isValidValue = typeof record.isValid === 'number' ? record.isValid === 1 : (record.isValid ?? false);
    form.setFieldsValue({
      ...record,
      isShow: isShowValue,
      isValid: isValidValue
    });
    setVisible(true);
  };

  // 删除
  const handleDelete = (id: number) => {
    setLoading(true);
    get(`/topic/delete/${id}`)
      .then((res: any) => {
        const { code, message } = res || {};
        if (code === 200) {
          Message.success('删除成功！');
          getList(false, current, pageSize);
        } else {
          Message.error(message || '删除失败');
        }
      })
      .catch(() => {
        Message.error('网络错误!');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // 提交表单
  const handleSubmit = () => {
    form
      .validate()
      .then((values) => {
        setConfirmLoading(true);
        const url = isEdit ? '/topic/update' : '/topic/add';
        const data: any = {
          ...values,
          isShow: values.isShow ? 1 : 0,
          isValid: values.isValid !== undefined ? (values.isValid ? 1 : 0) : 1,
          creatorId: userInfo.username || userInfo.nickName || ''
        };
        if (isEdit) {
          data.id = editId;
        }
        post(url, data)
          .then((res: any) => {
            const { code, message } = res || {};
            if (code === 200) {
              Message.success(isEdit ? '编辑成功！' : '新增成功！');
              handleCancel();
              getList();
            } else {
              Message.error(message || '操作失败');
            }
          })
          .catch(() => {
            Message.error('网络错误!');
          })
          .finally(() => {
            setConfirmLoading(false);
          });
      })
      .catch(() => {
        Message.error('请检查表单填写是否正确!');
      });
  };

  // 取消
  const handleCancel = () => {
    setVisible(false);
    setConfirmLoading(false);
    form.resetFields();
    setIsEdit(false);
    setEditId(null);
  };

  // 处理赛季变化
  const handleSessionChange = (value: string) => {
    setGameSession(value);
    setCurrent(1);
  };

  // 查询
  const handleSearch = () => {
    getList(true, 1, pageSize);
  };

  // 分页变化
  const handlePageChange = (page: number, size?: number) => {
    setCurrent(page);
    if (size && size !== pageSize) {
      setPageSize(size);
      getList(false, page, size);
    } else {
      getList(false, page, size || pageSize);
    }
  };

  // 初始化
  useEffect(() => {
    getSessionList();
  }, []);

  // // 当赛季列表加载完成后，设置默认赛季并加载数据
  // useEffect(() => {
  //   if (sessionList.length > 0 && !gameSession) {
  //     const defaultSession = sessionList[0].value;
  //     setGameSession(defaultSession);
  //   }
  // }, [sessionList]);

  // 赛季变化时获取话题列表
  useEffect(() => {
      getList(true, 1, pageSize);
  }, []);

  return (
    <div className="topics table-flex">
      <Space style={{ flexWrap: 'wrap', marginBottom: 16 }}>
        <div className="filter-item">
          <Typography.Text>话题名称:</Typography.Text>
          <Input
            value={topicNameFilter}
            onChange={(value) => setTopicNameFilter(value)}
            style={{ width: 200 }}
            allowClear
            placeholder="请输入..."
          />
        </div>
        <div className="filter-item">
          <Typography.Text>赛季:</Typography.Text>
          <Select
            value={gameSession}
            onChange={handleSessionChange}
            style={{ width: 120 }}
            allowClear
            placeholder="请选择"
          >
            {sessionList.map(item => (
              <Option value={item.value} key={item.value}>
                {item.label}
              </Option>
            ))}
          </Select>
        </div>
        <Button
          className="filter-item"
          type="primary"
          onClick={handleSearch}
          loading={loading}
        >
          查询
        </Button>
        <Button
          className="filter-item"
          type="primary"
          onClick={handleAdd}
        >
          新建
        </Button>
      </Space>
      <div className="table-content">
        <Table
          loading={loading}
          columns={columns}
          data={dataList}
          pagination={{
            current,
            pageSize,
            total,
            sizeCanChange: true,
            sizeOptions: [10, 20, 50, 100],
            showTotal: true,
            onChange: handlePageChange,
            onPageSizeChange: (size: number) => handlePageChange(1, size)
          }}
          rowKey="id"
          className="table-scroll"
          scroll={{ y: '100%', x: '100%' }}
        />
      </div>
      <Modal
        title={isEdit ? '编辑话题' : '新增话题'}
        visible={visible}
        onOk={handleSubmit}
        confirmLoading={confirmLoading}
        onCancel={handleCancel}
        style={{ width: 600 }}
      >
        <Form {...formItemLayout} form={form}>
          <FormItem
            label="赛季"
            field="gameSession"
            rules={[{ required: true, message: '请选择赛季' }]}
          >
            <Select
              placeholder="请选择"
              allowClear
            >
              {sessionList.map(item => (
                <Option value={item.value} key={item.value}>
                  {item.label}
                </Option>
              ))}
            </Select>
          </FormItem>
          <FormItem
            label="话题名称"
            field="topicName"
            rules={[{ required: true, message: '请输入话题名称' }]}
          >
            <Input placeholder="请输入..." />
          </FormItem>
          {/* <FormItem
            label="话题类目"
            field="category"
          >
            <Input placeholder="请输入..." />
          </FormItem> */}
          <FormItem
            label="是否可见"
            field="isShow"
            triggerPropName="checked"
          >
            <Switch checkedText="可见" uncheckedText="不可见" />
          </FormItem>
        </Form>
      </Modal>
    </div>
  );
}

export default Topics;

